/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useAI } from "../contexts/AIContext";
import { PUBLISHER_LABEL, COPYRIGHT_YEAR } from "../config/legal";
import {
  Zap, LayoutDashboard, BarChart2, TrendingUp, FileText, CheckCircle, MessageSquareWarning,
  CreditCard, ShoppingCart, Link2, Plug, Receipt, Globe,
  Send, Trophy, ClipboardList, Upload, Package, Handshake, Bot,
  FolderOpen, RefreshCw, Hammer, Scale, Wrench, GitMerge, UserCheck,
  Lock, Puzzle, Building2, Globe2, ShieldCheck,
  Sun, Moon, LogOut, ChevronsUpDown, PieChart, Brain,
  Layers, FileStack, Wallet, BarChart, UserCog, Star, CalendarDays,
  MessageSquare, ChevronRight, Users, ListChecks, Calculator,
} from "lucide-react";


/**
 * Remplace le bloc NAV_SECTIONS + BADGE_COLORS existant dans Sidebar.jsx
 * par celui-ci. Aucun `id` n'a changé -> aucun impact sur App.jsx (PAGES)
 * ni sur les onNavigate() ailleurs dans le code.
 *
 * Seul changement fonctionnel : tous les badges "NEW" utilisent
 * désormais un seul token (`new`) au lieu d'être éparpillés entre
 * success / info / accent selon l'item.
 */
 
const NAV_SECTIONS = [
  {
    id: "pilotage_section", section: "Pilotage",
    items: [
      { id: "demarrage", label: "Démarrage",           Icon: ListChecks },
      { id: "monmois",   label: "Mon mois comptable",  Icon: CalendarDays },
      { id: "dashboard", label: "Tableau de bord",     Icon: LayoutDashboard },
      { id: "analytics", label: "Analytique avancé",   Icon: BarChart2 },
      { id: "forecast",  label: "Prévis. trésorerie",   Icon: Brain },
      { id: "budget",    label: "Budget & Prévisionnel",Icon: BarChart, badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "ventes_section", section: "Ventes",
    items: [
      { id: "facturation", label: "Facturation",           Icon: FileText },
      { id: "clients",     label: "Clients",                Icon: Building2 },
      { id: "factelec",    label: "Fact. électronique",    Icon: CheckCircle, badge: "PA",  badgeColor: "success" },
      { id: "paiements",   label: "Paiements",              Icon: CreditCard },
      { id: "commandes",   label: "Commandes clients",      Icon: FileStack,   badge: "NEW", badgeColor: "new" },
      { id: "relances",    label: "Relances automatiques",  Icon: Send },
      { id: "abonnements", label: "Abonnements récurrents", Icon: RefreshCw,   badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "achats_section", section: "Achats & stocks",
    items: [
      { id: "achats", label: "Achats",             Icon: ShoppingCart },
      { id: "stocks", label: "Gestion des stocks", Icon: Package, badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "tresorerie_section", section: "Trésorerie & banque",
    items: [
      { id: "tresorerie",      label: "Trésorerie",        Icon: Wallet },
      { id: "banque",          label: "Comptes bancaires", Icon: Link2 },
      { id: "integrations",    label: "Intégrations",      Icon: Plug },
    ],
  },
  {
    id: "compta_section", section: "Comptabilité & fiscal",
    items: [
      { id: "comptabilite",     label: "Comptabilité",           Icon: TrendingUp },
      { id: "synthesetva",      label: "Synthèse TVA",           Icon: Calculator },
      { id: "plancomptable",    label: "Plan comptable",         Icon: ClipboardList },
      { id: "fec",              label: "FEC & exports",          Icon: Upload },
      { id: "comptaanalytique", label: "Compta. analytique",     Icon: PieChart, badge: "NEW", badgeColor: "new" },
      { id: "tva",              label: "TVA intracommunautaire", Icon: Globe },
      { id: "immobilisations",  label: "Immobilisations",        Icon: Hammer,   badge: "NEW", badgeColor: "new" },
      { id: "ndf",              label: "Notes de frais",         Icon: Receipt },
    ],
  },
  {
    id: "commercial_section", section: "Commercial",
    items: [
      { id: "crm",           label: "CRM",             Icon: Handshake,    badge: "NEW", badgeColor: "new" },
      { id: "prospectionia", label: "Prospection IA",  Icon: Bot,          badge: "IA",  badgeColor: "accent" },
      { id: "agenda",        label: "Agenda & RDV",    Icon: CalendarDays, badge: "NEW", badgeColor: "new" },
      { id: "portal",        label: "Portail client",  Icon: Globe2,       badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "conformite_section", section: "Conformité & documents",
    items: [
      { id: "veillereglem",     label: "Veille Réglementaire",   Icon: Scale,      badge: "NEW", badgeColor: "new" },
      { id: "multilingues",     label: "Documents multilingues", Icon: Globe },
      { id: "documentsarchive", label: "Espace documentaire",    Icon: FolderOpen, badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "groupe_section", section: "Groupe",
    items: [
      { id: "groupe",     label: "Vue groupe",          Icon: Layers,   badge: "CONSO", badgeColor: "info" },
      { id: "societes",   label: "Sociétés",             Icon: Building2 },
      { id: "migration",  label: "Tableau migration",    Icon: GitMerge, badge: "NEW", badgeColor: "new" },
      { id: "rattrapage", label: "Rattrapage 2026",      Icon: Wrench,   badge: "NEW", badgeColor: "new" },
      { id: "fusion",     label: "Fusion de sociétés",   Icon: Layers,   badge: "NEW", badgeColor: "new" },
    ],
  },
  {
    id: "admin_section", section: "Administration",
    items: [
      { id: "expertcomptable", label: "Expert-comptable",    Icon: UserCheck, badge: "NEW", badgeColor: "new" },
      { id: "securite",        label: "Sécurité & accès",    Icon: Lock },
      { id: "equipe",           label: "Gestion d'équipe",    Icon: Users, badge: "NEW", badgeColor: "new" },
      { id: "modules",         label: "Modules & config",    Icon: Puzzle },
      { id: "adminsaas",       label: "Administration SaaS", Icon: UserCog, superadminOnly: true },
      { id: "signalements",    label: "Signalements",        Icon: MessageSquareWarning, superadminOnly: true },
      { id: "monabonnement",   label: "Mon abonnement",      Icon: Star },
      { id: "tarifs",          label: "Tarifs & abonnement", Icon: CreditCard },
    ],
  },
];
 
// Un seul token ajouté à l'existant : "new" (les 5 autres sont inchangés).
// Couleur laiton/or de la maquette "intermédiaire" -- à ajuster si tu
// préfères réutiliser "info" ou "accent" tel quel pour éviter tout ajout.
const BADGE_COLORS = {
  success: { bg: 'rgba(16,185,129,0.18)',  text: '#10B981' },
  warning: { bg: 'rgba(245,158,11,0.18)',  text: '#F59E0B' },
  danger:  { bg: 'rgba(239,68,68,0.18)',   text: '#EF4444' },
  info:    { bg: 'rgba(59,130,246,0.18)',  text: '#3B82F6' },
  accent:  { bg: 'rgba(59,130,246,0.18)',  text: '#60A5FA' }, // bleu distinct — évite confusion avec success (vert)
  new:     { bg: 'rgba(184,147,76,0.18)',  text: '#B8934C' },
};
 


// Divulgation progressive (partie 3 de l'onboarding personnalisé) — les
// sections correspondant à au moins une priorité choisie au questionnaire
// restent affichées normalement, en premier ; le reste est regroupé dans
// une section unique "Découvrir d'autres fonctionnalités", repliée par
// défaut. Ne retire aucun accès réel — items inchangés, juste réordonnés.
// Sections non couvertes par une clé de priorité (Pilotage, Trésorerie,
// Conformité, Groupe, Administration) restent toujours affichées
// normalement — ce ne sont pas des choix d'activité mais des fondations.
const PRIORITY_TO_SECTIONS = {
  facturation:  ["ventes_section"],
  comptabilite: ["compta_section"],
  stock:        ["achats_section"],
  achats:       ["achats_section"],
  factelec:     ["ventes_section"],
  crm:          ["commercial_section"],
};
const DISCOVER_SECTION_ID = "discover_section";

function computeDisplaySections(priorities) {
  if (!Array.isArray(priorities) || priorities.length === 0) return NAV_SECTIONS;

  const prioritySectionIds = new Set(
    priorities.flatMap(p => PRIORITY_TO_SECTIONS[p] || [])
  );
  const groupableIds = new Set(Object.values(PRIORITY_TO_SECTIONS).flat());

  const priority = [];
  const discoverItems = [];
  for (const section of NAV_SECTIONS) {
    if (!groupableIds.has(section.id) || prioritySectionIds.has(section.id)) {
      priority.push(section);
    } else {
      discoverItems.push(...section.items);
    }
  }
  if (discoverItems.length === 0) return priority;
  return [...priority, { id: DISCOVER_SECTION_ID, section: "Découvrir d'autres fonctionnalités", items: discoverItems }];
}

const getInitialOpen = (currentPage, displaySections) => {
  const state = {};
  displaySections.forEach(s => {
    const hasActive = s.items.some(i => i.id === currentPage);
    try {
      const saved = localStorage.getItem("sidebar_open_" + s.id);
      state[s.id] = saved !== null ? saved === "true" : hasActive;
    } catch {
      state[s.id] = hasActive;
    }
  });
  return state;
};

export default function Sidebar({ currentPage, onNavigate, open, companies = [], currentCompany, onCompanyChange, onLogout, countBadges = {}, priorities = [] }) {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { openAI } = useAI();
  const [showSocPicker, setShowSocPicker] = useState(false);
  const displaySections = computeDisplaySections(priorities);
  const [openSections, setOpenSections] = useState(() => getInitialOpen(currentPage, displaySections));
  const toggleSection = (id) => {
  setOpenSections(prev => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem("sidebar_open_" + id, String(next[id])); } catch {}
    return next;
  });
};
  if (!open) return null;

  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
    : "??";

  return (
    <aside style={{
      position: "fixed", left: 0, top: 0, bottom: 0, width: "var(--sidebar-width, 240px)",
      background: "var(--bg-sidebar)", color: "#e2e8f0", display: "flex",
      flexDirection: "column", overflowY: "hidden", zIndex: 100,
      borderRight: "1px solid rgba(255,255,255,0.06)",
    }}>

      {/* ── Logo ─────────────────────────────────── */}
      <div style={{ padding: "16px 16px 14px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
        <img src="/icon_tight.png" alt="ExecPro360" style={{ width: 60, height: 60, flexShrink: 0, objectFit: "contain" }} />
        <div style={{ flex: 1, minWidth: 0, marginTop: 6 }}>
          <img src="/logo_wordmark_only.png" alt="ExecPro360" style={{ width: 95, maxWidth: "100%", height: "auto", display: "block", objectFit: "contain" }} />
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 6 }}>Pilotez. Décidez. Avancez.</div>
        </div>
      </div>

      {/* ── Sélecteur société ─────────────────────── */}
      <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
        <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", marginBottom: 5 }}>Périmètre actif</div>
        <div
          onClick={() => setShowSocPicker(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 9px", background: "rgba(26,158,114,0.1)", border: "1px solid rgba(26,158,114,0.2)", borderRadius: 8, cursor: "pointer" }}
        >
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#1a9e72", flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "#34D399", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentCompany ? currentCompany.name : "Chargement…"}
          </span>
          <span style={{ fontSize: 9, background: "rgba(26,158,114,0.2)", color: "#34D399", padding: "2px 6px", borderRadius: 4, fontWeight: 700, flexShrink: 0 }}>GROUPE</span>
          <ChevronsUpDown size={13} color="#64748b" />
        </div>
        {showSocPicker && companies.length > 0 && (
          <div style={{ marginTop: 5, background: "#0d1117", borderRadius: 8, border: "1px solid rgba(255,255,255,0.07)", overflow: "hidden" }}>
            {companies.map(s => (
              <div key={s.id} onClick={() => { onCompanyChange(s); setShowSocPicker(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", cursor: "pointer", background: currentCompany?.id === s.id ? "rgba(26,158,114,0.1)" : "transparent" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1a9e72" }} />
                <span style={{ fontSize: 11, color: currentCompany?.id === s.id ? "#34D399" : "#94a3b8", flex: 1 }}>{s.name}</span>
                <span style={{ fontSize: 10, color: "#475569" }}>{s.treso ? `${(s.treso / 1000).toFixed(0)}k€` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Navigation ───────────────────────────── */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "6px 8px" }}>
  {displaySections.map(section => {
    const isOpen = openSections[section.id];
    const hasActive = section.items.some(i => i.id === currentPage);
    return (
      <div key={section.id}>
        <div
          onClick={() => toggleSection(section.id)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 6px 5px", cursor: "pointer", userSelect: "none",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          <span style={{
            fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em",
            color: hasActive ? "#34D399" : "#6B7280", fontWeight: 700,
          }}>
            {section.section}
          </span>
          <ChevronRight
            size={12}
            color={hasActive ? "#34D399" : "#4B5563"}
            style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
          />
        </div>
        <div style={{
          overflow: "hidden",
          maxHeight: isOpen ? "600px" : "0px",
          transition: "max-height 0.25s ease",
        }}>
          {section.items.map(item => {
            if (item.superadminOnly && !user?.is_superadmin) return null;
            const isActive = currentPage === item.id;
            const { Icon } = item;
            // Compteur réel (29/10/2026) : ce qui reste à faire, avec sa bulle d'aide ; jamais de nombre écrit en dur
            const counter = countBadges[item.id];
            const badgeValue = counter ? (counter.count > 0 ? String(counter.count) : null) : item.badge;
            const bc = counter ? BADGE_COLORS.warning : item.badgeColor ? BADGE_COLORS[item.badgeColor] : null;
            return (
              <div
                key={item.id}
                onClick={() => onNavigate(item.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 9,
                  padding: "8px 10px 8px 14px",
                  cursor: "pointer", borderRadius: 8, marginBottom: 1,
                  color: isActive ? "#fff" : "#e2e8f0",
                  fontSize: 13.5, fontWeight: isActive ? 700 : 500,
                  background: isActive ? "#1a9e72" : "transparent",
                  borderLeft: `3px solid ${isActive ? "#34D399" : "transparent"}`,
                  transition: "all 0.13s",
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = "rgba(26,158,114,0.15)";
                    e.currentTarget.style.color = "#FFFFFF";
                    e.currentTarget.style.borderLeft = "3px solid rgba(26,158,114,0.5)";
                  } else {
                    e.currentTarget.style.background = "#0f6e56";
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#e2e8f0";
                    e.currentTarget.style.borderLeft = "3px solid transparent";
                  } else {
                    e.currentTarget.style.background = "#1a9e72";
                  }
                }}
              >
                {Icon && <Icon size={15} strokeWidth={isActive ? 2.5 : 1.8} style={{ flexShrink: 0 }} />}
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                {badgeValue && bc && (
                  <span style={{ fontSize: item.badgeColor === "accent" ? 10 : 9, padding: item.badgeColor === "accent" ? "2px 6px" : "1px 5px", borderRadius: 8, fontWeight: 700, background: bc.bg, color: bc.text, flexShrink: 0 }} title={counter?.title}>{badgeValue}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })}
</nav>

      {/* ── Footer ───────────────────────────────── */}
      <div style={{ padding: "10px 12px", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>

        {/* Badge sécurité */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)", borderRadius: 6, padding: "4px 8px", marginBottom: 8 }}>
          <ShieldCheck size={12} color="#10B981" />
          <span style={{ fontSize: 10, color: "#10B981", fontWeight: 500 }}>2FA · TLS 1.3 · RGPD</span>
        </div>

        {/* Bouton Assistant IA */}
        <div
          onClick={() => openAI(currentPage)}
          style={{
            display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
            cursor: "pointer", borderRadius: 8, marginBottom: 8,
            color: "#94a3b8", fontSize: 12, fontWeight: 400,
            background: "transparent",
            borderLeft: "3px solid transparent",
            transition: "all 0.13s",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(26,158,114,0.15)";
            e.currentTarget.style.color = "#FFFFFF";
            e.currentTarget.style.borderLeft = "3px solid rgba(26,158,114,0.5)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#94a3b8";
            e.currentTarget.style.borderLeft = "3px solid transparent";
          }}
        >
          <MessageSquare size={16} strokeWidth={1.8} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>Assistant IA</span>
          <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, fontWeight: 700, background: "rgba(59,130,246,0.18)", color: "#60A5FA", flexShrink: 0 }}>IA</span>
        </div>

        {/* Utilisateur + actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, background: "#1a9e72",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "Utilisateur"}</div>
            <div style={{ fontSize: 10, color: "#475569" }}>
              {user?.role === "admin" ? "Admin · DG" : user?.role === "comptable" ? "Comptable" : user?.role}
            </div>
          </div>
          {/* Déconnexion */}
          <button onClick={onLogout} title="Déconnexion" style={{ background: "none", border: "none", cursor: "pointer", color: "#475569", display: "flex", padding: 4, borderRadius: 6, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
            onMouseLeave={e => e.currentTarget.style.color = "#475569"}>
            <LogOut size={15} />
          </button>
        </div>

        {/* Toggle thème */}
        <div
          onClick={toggle}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8, cursor: "pointer", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
        >
          {theme === "dark"
            ? <Sun size={14} color="#F59E0B" />
            : <Moon size={14} color="#818CF8" />}
          <span style={{ fontSize: 11, color: "#64748b", flex: 1 }}>
            {theme === "dark" ? "Mode clair" : "Mode sombre"}
          </span>
          <div style={{ width: 28, height: 16, borderRadius: 8, background: theme === "dark" ? "#6366F1" : "#374151", position: "relative", transition: "background 200ms", flexShrink: 0 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: theme === "dark" ? 14 : 2, transition: "left 200ms" }} />
          </div>
        </div>

        {/* Copyright */}
        <div style={{ marginTop: 8, textAlign: "center", fontSize: 9, color: "#1e293b", lineHeight: 1.6 }}>
          © {COPYRIGHT_YEAR} {PUBLISHER_LABEL} · Tous droits réservés
        </div>
      </div>
    </aside>
  );
}
